import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useMemo, useState } from 'react';
import { Button } from 'antd';
import { USER_PROFILE_DEFAULT_AVATAR_IMAGE } from 'assets/images/images';
import { Stack } from 'components/stack/stack';
import TwoColumnSection from 'components/two-column-section/TwoColumnSection';
import { OfferResponse } from 'logic/api-models/api-models';
import { useBreakpoints } from 'logic/hooks/use-breakpoints';
import { getImageSrc } from 'logic/utils/image';
import { Seo } from 'components/seo/seo';
import { Text } from 'components/text/Text';
import { StarsRating } from 'components/rating/StarsRating';
import {
  MAX_RATINGS_PREVIEW_COUNT_LARGE,
  MAX_RATINGS_PREVIEW_COUNT_MOBILE,
} from 'logic/configs/ratings';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { ContentContainer } from '../../../components/content-container/content-container';
import { TutorDescription } from './components/TutorDescription';
import { TutorInfo } from './components/TutorInfo';
import { useTutorInfo } from './useTutorInfo';
import { UserReviewList } from 'components/rating/UserReviewList';
import { modalSlice } from 'logic/store/modal/modal.slice';
import { RatingBadge } from 'components/rating/RatingBadge';
import { RatingBadgesGroup } from 'components/rating/RatingBadgesGroup';
import { BadgesNames } from 'models/badges';
import { getRatingsWithComment } from 'logic/utils/ratings';
import { RequestTutoringModal } from './components/RequestTutoringModal';
import { offerSlice } from 'logic/store/offer/offer.slice';
import { ReportOfferModal } from './components/ReportOfferModal';
import getIsTutoringSchool from 'logic/helpers/isTutoringSchool';
import { arrowsPlugin } from 'brainhubeu-react-carousel';
import { LEFT, RIGHT } from 'assets/icons/slider/slider';
import { RateOfferModal } from './components/RateOfferModal';
import { getAuthenticatedUser } from 'logic/store/authentication/authentication.selectors';
import { AuthenticationStepKey } from 'logic/store/authentication/authentication.model';
import 'brainhubeu-react-carousel/lib/style.css';
import { isTutoringSchoolOffer } from 'logic/store/offer/offer.logic';
import { AppRoute } from 'app/app.route.const';
import { ImageFitContain, VideoFitContain } from './components/commons';
import { featureFlags } from 'logic/feature-flag/feature-flag';
import { OnlineTutorDetailSectionPromotion } from 'components/InfoVideoBox/defaults/OnlineTutorDetailSectionPromotion';
import { OnlineTutorSchoolDetailSectionPromotion } from 'components/InfoVideoBox/defaults/OnlineTutorSchoolDetailSectionPromotion';
import loadable from '@loadable/component';
import { formatAverageRating } from 'logic/utils/number';
import { Ad } from 'components/ad/ad';
import { AdProvider } from 'components/ad/ad-provider';
import { AD_SIZE_SMALL } from 'components/ad/ad.consts';
import {
  AdPlaceholder,
  CounterGallery,
  LeftColumn,
  RatingWrapper,
  ReportOfferWrapper,
  RightColumn,
  StyledSlider,
  WrapperGallery,
} from 'src/pages-all/details/tutor/TutorDetails.style';
import { isElitetutor, isRefugeeTutor } from 'logic/helpers/tutorHelper';
import { useLocation } from 'react-use';
import { useLocalizedI18nGlobal } from '../../../logic/translations/use-localized-i18n-global';

const TutorAproximateLocationMap = loadable(() => import('./components/tutor-map/tutor-map'));

const TutorDetailsPage: React.FC<{
  offer: OfferResponse | undefined;
  actionButton: React.ReactNode;
}> = ({ offer, actionButton }) => {
  useLocalizedI18nGlobal();
  const { min1024, minTablet } = useBreakpoints();
  const {
    imageUrl,
    tutorNameLabel,
    isMyOffer,
    userRatings,
    badges,
    totalRatings,
    averageRating,
  } = useTutorInfo({ offer });
  const dispatch = useDispatch();
  const TutoringSchool = getIsTutoringSchool(offer);
  const location = useLocation();
  const _isElitetutor = isElitetutor(offer, location?.pathname);
  const _isRefugeeTutor = isRefugeeTutor(offer, location?.pathname);
  const getDefault = (item: any) => item.isDefault;
  const [galleryIdx, setgalleryIdx] = useState(
    offer?.tutoringSchool?.images?.findIndex(getDefault)
  );
  const authenticatedUser = useSelector(getAuthenticatedUser);

  const ratingsWithComment = useMemo(() => getRatingsWithComment(userRatings), [userRatings]);

  const numOfPreviewReviews = minTablet
    ? MAX_RATINGS_PREVIEW_COUNT_LARGE
    : MAX_RATINGS_PREVIEW_COUNT_MOBILE;

  const onChangeGallery = (idx: number) => {
    if (idx >= 0 && offer?.tutoringSchool?.images && idx < offer?.tutoringSchool?.images?.length)
      setgalleryIdx(idx);
  };
  const arrows = [
    'fastSwipe',
    {
      resolve: arrowsPlugin,
      options: {
        arrowLeft: <img src={LEFT} loading="lazy" alt="left" />,
        arrowLeftDisabled: <></>,
        arrowRight: <img src={RIGHT} loading="lazy" alt="right" />,
        arrowRightDisabled: <></>,
        addArrowClickHandler: true,
      },
    },
  ];

  const renderGallery = useCallback(() => {
    const images = offer?.tutoringSchool?.images;
    return TutoringSchool ? (
      <WrapperGallery min1024={min1024}>
        <StyledSlider
          min1024={min1024}
          plugins={min1024 ? arrows : ['fastSwipe']}
          value={galleryIdx}
          onChange={onChangeGallery}
        >
          {images?.map((image, idx) => (
            <ImageFitContain
              key={idx}
              width={'100%'}
              loading="lazy"
              height={'100%'}
              style={{ maxHeight: min1024 ? '370px' : '320px', overflow: 'hidden' }}
              src={getImageSrc(image?.url)}
              preview={false}
              fallback={USER_PROFILE_DEFAULT_AVATAR_IMAGE}
              alt={tutorNameLabel}
            />
          ))}
        </StyledSlider>
        <CounterGallery min1024={min1024}>
          <Text color="white" size={14}>
            {galleryIdx !== undefined ? galleryIdx + 1 : 0} / {images?.length}
          </Text>
        </CounterGallery>
      </WrapperGallery>
    ) : (
      renderMedia()
    );
  }, [TutoringSchool, offer?.tutoringSchool?.images, min1024, galleryIdx, onChangeGallery]);

  const renderMedia = () => {
    const video = offer?.profileVideoUrls?.mp4_480p?.url;
    const thumbnail = offer?.profileVideoUrls?.thumb?.url;

    if (video)
      return (
        <VideoFitContain
          width={'100%'}
          height={'100%'}
          style={{ maxHeight: min1024 ? '370px' : '320px', overflow: 'hidden' }}
          src={video}
          controls={true}
          preload={'metadata'}
          poster={thumbnail}
        />
      );

    return (
      <ImageFitContain
        width={'auto'}
        height={'100%'}
        loading="lazy"
        style={{ maxHeight: min1024 ? '370px' : '320px', overflow: 'hidden' }}
        src={getImageSrc(imageUrl)}
        fallback={USER_PROFILE_DEFAULT_AVATAR_IMAGE}
        alt={tutorNameLabel}
        preview={false}
      />
    );
  };

  const handleOnClickReport = () => {
    dispatch(modalSlice.actions.openReportOfferModal());
  };

  const handleOnClickRating = () => {
    if (!authenticatedUser) {
      dispatch(
        modalSlice.actions.openAuthenticationModal({
          stepKey: AuthenticationStepKey.Login,
        })
      );
    } else {
      dispatch(modalSlice.actions.openGiveRatingModal());
    }
  };

  const renderBadgesIfCan = () => {
    const hasAnyBadge = Object.values(badges).some((count) => count || 0 > 0);

    return (
      hasAnyBadge && (
        <RatingBadgesGroup inline>
          {Object.entries(badges)
            .map(
              ([badge, count], idx) =>
                count && <RatingBadge key={idx} badge={badge as BadgesNames} count={count} />
            )
            .filter(Boolean)}
        </RatingBadgesGroup>
      )
    );
  };

  const renderTutorStarsIfCan = () =>
    totalRatings > 0 && (
      <RatingWrapper gap={1}>
        <Text size={14} color={'primary'} strong>
          {translate(translationKeys.tutorDetails.info.reviews)} ({totalRatings})
        </Text>
        <StarsRating value={averageRating} allowChange={false} size={14} spaceStars={4} />
        <Text color={'primary'} strong>
          {formatAverageRating(averageRating)}
        </Text>
      </RatingWrapper>
    );

  const renderTutorReviewsIfCan = () =>
    ratingsWithComment.length > 0 && (
      <UserReviewList
        userRatings={ratingsWithComment.slice(0, numOfPreviewReviews)}
        columns={minTablet ? 2 : 1}
      />
    );

  const renderShowAllReviewsButtonIfCan = () =>
    numOfPreviewReviews < ratingsWithComment.length && (
      <div>
        <Button onClick={() => dispatch(modalSlice.actions.openReviewsModal())}>
          <Text size={16} fontWeight={500}>
            {translate(translationKeys.ratings.showAllReviewsCTApt1)}
            {` ${ratingsWithComment.length} `}
            {translate(translationKeys.ratings.showAllReviewsCTApt2)}
          </Text>
        </Button>
      </div>
    );

  const renderReportOfferCTA = () =>
    !isMyOffer && (
      <ReportOfferWrapper>
        <Text
          fontWeight={500}
          color={'primary'}
          style={{ cursor: 'pointer' }}
          onClick={handleOnClickReport}
        >
          {translate(translationKeys.reportOffer.reportCTA)}
        </Text>
      </ReportOfferWrapper>
    );

  const renderRatingButton = () =>
    !isMyOffer && (
      <Button
        type="primary"
        size="middle"
        onClick={handleOnClickRating}
        style={{ maxWidth: min1024 ? 270 : '100%', height: 40, marginBottom: 14 }}
      >
        {translate(translationKeys.rateTooltips.rateSchool)}
      </Button>
    );

  const renderTutorRatings = () =>
    !isMyOffer && (
      <Stack gap={1} flow={'row'} style={{ marginBottom: 24 }}>
        {renderTutorStarsIfCan()}
        {TutoringSchool && renderRatingButton()}
        {!TutoringSchool && renderBadgesIfCan()}
        {renderTutorReviewsIfCan()}
        {renderShowAllReviewsButtonIfCan()}
      </Stack>
    );

  const renderRemoveOffer = () => {
    const onClick = () => dispatch(offerSlice.actions.tryRemoveOffer(offer?._id));

    return (
      isMyOffer && (
        <Text
          style={{ cursor: 'pointer', marginTop: 16 }}
          fontWeight={500}
          textAlign={'center'}
          onClick={onClick}
        >
          {translate(translationKeys.userProfile.deleteOffer.deleteOfferCTA).toUpperCase()}
        </Text>
      )
    );
  };

  return (
    <ContentContainer background="white">
      <AdProvider>
        {!isTutoringSchoolOffer(offer) && (
          <Seo
            title={`Tutorspace.de - ${tutorNameLabel}`}
            description={offer?.offerDesc}
            image={imageUrl}
          />
        )}
        {isTutoringSchoolOffer(offer) && (
          <Seo
            title={translate(translationKeys.seoRoute[AppRoute.SchoolDetails].title, {
              schoolName: offer?.tutoringSchool?.name,
              city: offer?.location?.place,
            })}
            description={translate(translationKeys.seoRoute[AppRoute.SchoolDetails].description, {
              schoolName: offer?.tutoringSchool?.name,
              city: offer?.location?.place,
              postcode: offer?.location?.postcode,
            })}
            image={imageUrl}
          />
        )}
        {min1024 ? (
          <TwoColumnSection
            rightOnTop
            leftWidth="100%"
            left={
              <LeftColumn>
                <Stack gap={0.5} flow="row" fullWidth>
                  {renderGallery()}
                  <TutorDescription offer={offer} />
                </Stack>
              </LeftColumn>
            }
            right={
              <RightColumn>
                <Stack gap={0.5} flow="row" alignItems="start">
                  <AdPlaceholder>
                    <Stack gap={0.5} flow="row">
                      <TutorInfo offer={offer}>{actionButton}</TutorInfo>
                      {renderRemoveOffer()}
                      {!_isElitetutor && !_isRefugeeTutor && (
                        <Ad
                          adUnit={'web_offer_sidebar_banner'}
                          size={AD_SIZE_SMALL}
                          style={{
                            justifyContent: 'flex-start',
                            marginTop: isTutoringSchoolOffer(offer) ? '40px' : '0px',
                          }}
                        />
                      )}
                    </Stack>
                  </AdPlaceholder>
                </Stack>
              </RightColumn>
            }
          />
        ) : (
          <Stack gap={0.5} flow="row">
            {renderGallery()}
            <TutorInfo offer={offer}>{actionButton}</TutorInfo>
            {renderRemoveOffer()}
            <TutorDescription offer={offer} />
          </Stack>
        )}
        {!_isElitetutor && <TutorAproximateLocationMap offer={offer} />}
        {featureFlags.ratings && renderTutorRatings()}
        {renderReportOfferCTA()}
        <RequestTutoringModal />
        <ReportOfferModal />
        <RateOfferModal />
        {featureFlags.eliteTutorPromotion &&
          !_isElitetutor &&
          !_isRefugeeTutor &&
          (isTutoringSchoolOffer(offer) ? (
            <OnlineTutorSchoolDetailSectionPromotion />
          ) : (
            <OnlineTutorDetailSectionPromotion />
          ))}
        {!_isElitetutor && !_isRefugeeTutor && (
          <Ad adUnit={'web_offer_bottom_banner'} style={{ marginBottom: '40px' }} />
        )}
      </AdProvider>
    </ContentContainer>
  );
};

export default TutorDetailsPage;
